import "@momentum-ui/core/scss/momentum-ui.scss";
import { StyledEngineProvider, Theme, ThemeProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-material.css";
import { LicenseManager } from "ag-grid-enterprise";
import AIChatPortal from "components/app/AIChatPortal";
import Layout from "components/app/AppLayoutV2";
import { ActivityLookupContextProvider } from "components/playbook/context/ActivityLookupContext";
import { TemplateIdContextProvider } from "components/playbook/context/TemplateIdContext";
import { CI_EMAILID_COOKIE, CI_FIRST_NAME_COOKIE, LAST_NAME_COOKIE, ROLE_COOKIE } from "constant";
import { GlobalModelProvider } from "contexts/GlobalModelContext";
import "flatpickr/dist/themes/material_green.css";
import { AnimatePresence, motion } from "framer-motion";
import useGridToolPanelClickAway from "hooks/useGridToolPanelClickAway";
import "index.scss";
import { useAxios } from "myAxios";
import { AppProps } from "next/app";
import Head from "next/head";
import { AuthProvider } from "pages";
import { useEffect, useMemo } from "react";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import { Slide, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SWRConfig } from "swr";
import { lightTheme } from "theme";
import { Cookies } from "utils";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line
  interface DefaultTheme extends Theme {}
}

const fetcher = (url: string, headers?: string, addPaging?: boolean) => {
  const axios = headers ? useAxios({ headers }) : useAxios();
  if (addPaging) {
    return axios.get(url).then((res) => ({
      data: res.data,
      "total-elements": +res.headers["total-elements"] ?? 0,
      "total-pages": +res.headers["total-pages"] ?? 0,
    }));
  }
  return axios.get(url).then((res) => res.data);
};

export const postFetcher = ([url, body, headers]: [string, string, Record<string, string>?]) => {
  const axios = headers ? useAxios({ headerConfig: headers }) : useAxios();
  //   console.log("postFetcher called with url:", url, "and body:", body);
  return axios.post(url, JSON.parse(body)).then((res) => res.data);
};

export default function App({ Component, pageProps, router }: AppProps) {
  useGridToolPanelClickAway();

  LicenseManager.setLicenseKey(
    "CompanyName=XLsoft China_on_behalf_of_Cisco (China),LicensedApplication=CISCO,LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=2,LicensedProductionInstancesCount=0,AssetReference=AG-010766,ExpiryDate=25_September_2021_[v2]_MTYzMjUyNDQwMDAwMA==b5743f8de2ce37605e76a26b000940e6"
  );

  const publicPages = [
    // "/spng",
    "/spng/login",
    "/spng/signup",
    "/spng/error",
    "/spng/middle",
  ];
  const isPublicPage = publicPages.includes(router.pathname);

  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles) {
      jssStyles!.parentElement!.removeChild(jssStyles);
    }

    //for matomo to track url
    router.events.on("routeChangeComplete", () => {
      const _paq = (window as any)._paq ?? [];
      _paq.push([
        "setCustomVariable",
        1,
        Cookies.get(CI_EMAILID_COOKIE) ?? "Unknown",
        `${Cookies.get(CI_FIRST_NAME_COOKIE)} ${Cookies.get(LAST_NAME_COOKIE)}`,
        "visit",
      ]);
      _paq.push(["setCustomVariable", 2, "role", Cookies.get(ROLE_COOKIE) ?? "Unknown", "visit"]);
      _paq.push(["setCustomUrl", `${location.pathname}`]);
      _paq.push(["trackPageView"]);
      _paq.push(["enableLinkTracking"]);
    });
  }, []);

  const theme = useMemo(() => lightTheme, []);
  const isAssignPage = router.pathname === "/main/assignmentTool";

  return (
    <SWRConfig
      value={{
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        shouldRetryOnError: false,
        fetcher,
        onError: (err, key, config) => {
          console.error("[SWR] fetch error", { err, key, config });
        },
      }}
    >
      <StyledEngineProvider injectFirst>
        <GlobalModelProvider>
          <ThemeProvider theme={theme}>
            {/* @ts-ignore */}
            {!isPublicPage ? (
              <AuthProvider.Provider value={pageProps?.authDetail}>
                <ActivityLookupContextProvider>
                  <TemplateIdContextProvider>
                    <Head>
                      <title>Cloud Collaboration Success Portal</title>
                    </Head>
                    <CssBaseline />
                    <ToastContainer
                      transition={Slide}
                      closeButton={true}
                      autoClose={2000}
                    />
                    <Layout>
                      <AnimatePresence
                        mode="wait"
                        initial={false}
                      >
                        <motion.div
                          key={router.asPath}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          style={{
                            height: "100%",
                            borderRadius: 4,
                            margin: isAssignPage ? "-20px -24px auto -25px" : "auto",
                          }}
                          id={"content-wrapper"}
                        >
                          <Component {...pageProps} />
                          {/* <TodoList /> */}
                        </motion.div>
                      </AnimatePresence>
                    </Layout>
                    <AIChatPortal />
                  </TemplateIdContextProvider>
                </ActivityLookupContextProvider>
              </AuthProvider.Provider>
            ) : (
              <Component {...pageProps} />
            )}
          </ThemeProvider>
        </GlobalModelProvider>
      </StyledEngineProvider>
    </SWRConfig>
  );
}
